import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from 'utils/axiosInterceptors';
import { Button, Dropdown, DropdownButton, Form, Row } from 'react-bootstrap';
import { ColumnDef } from '@tanstack/react-table';
import { TProcurementReturns, TSupplierDetails } from 'utils/types';
import { formatDate } from 'utils/formats';
import { useBranchContext } from 'providers/BranchProvider';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  CustomModalProps,
  default as CustomModal,
  ModalHandle
} from 'components/modals/CustomModal';
import DataTable from '../BranchProductInventory/DataTable';
import BadgeStatus from 'utils/BadgeStatus';
import CreateReturnDetailsForm, {
  CreateReturnDetailsFormHandle
} from './CreateReturnDetailsForm';
import ValidateReturnDetailsForm from './ValidateReturnDetailsForm';
import ReturnToSupplierDetailsForm from './ReturnToSupplierDetailsForm';
import CancelReturnDetailsForm from './CancelReturnDetailsForm';
import RejectReturnDetailsForm from './RejectReturnDetailsForm';

type ProcurementReturnFilters = {
  [key: string]: string | undefined;
  supplierId: string | undefined;
  status: string | undefined;
};

const ReturnsListTab = () => {
  const { selectedBranch } = useBranchContext();
  const [suppliers, setSuppliers] = useState<TSupplierDetails[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const [filters, setFilters] = useState<ProcurementReturnFilters>({
    supplierId: queryParams.get('supplierId') || undefined,
    status: queryParams.get('status') || undefined
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterChange = (key: string, value: any) => {
    const newFilters = {
      ...filters,
      [key]: value
    };
    setFilters(newFilters);
    const newQueryParams = new URLSearchParams(location.search);
    Object.keys(newFilters).forEach(filterKey => {
      if (newFilters[filterKey]) {
        newQueryParams.set(filterKey, newFilters[filterKey] as string);
      } else {
        newQueryParams.delete(filterKey);
      }
    });
    navigate({ search: newQueryParams.toString() });
  };
  const [isSubmitted, setIsSubmitted] = useState(false);
  const returnStatus = [
    { value: 'New', label: 'New' },
    { value: 'Reviewed', label: 'Reviewed' },
    { value: 'Refunded', label: 'Refunded' },
    { value: 'Cancelled', label: 'Cancelled' },
    { value: 'Rejected', label: 'Rejected' },
    { value: 'ReturnedToSupplier', label: 'Returned' }
  ];

  const [modalProps, setModalProps] = useState<CustomModalProps>();
  const modalRef = useRef<ModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const addProcurementReturnFormRef =
    useRef<CreateReturnDetailsFormHandle>(null);
  const validateProcurementReturnFormRef =
    useRef<CreateReturnDetailsFormHandle>(null);
  const returnToSupplierDetailsFormRef =
    useRef<CreateReturnDetailsFormHandle>(null);
  const cancelReturnDetailsFormRef =
    useRef<CreateReturnDetailsFormHandle>(null);
  const rejectReturnDetailsFormRef =
    useRef<CreateReturnDetailsFormHandle>(null);

  const createProcurementReturn = () => {
    setModalProps({
      title: 'Create Return',
      content: (
        <CreateReturnDetailsForm
          ref={addProcurementReturnFormRef}
          suppliers={suppliers}
        />
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleSubmitProcurementReturn()
    });
    modalRef.current.show();
  };

  const handleSubmitProcurementReturn = async () => {
    if (addProcurementReturnFormRef.current) {
      try {
        const isSuccess = await addProcurementReturnFormRef.current.submit();
        if (isSuccess) {
          modalRef.current.hide();
          setIsSubmitted(true);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const validateProcurementReturn = (procurementReturnId: number) => {
    setModalProps({
      title: 'Validate Return',
      content: (
        <ValidateReturnDetailsForm
          ref={validateProcurementReturnFormRef}
          procurementReturnId={procurementReturnId}
        />
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleSubmitValidatedProcurementReturn()
    });
    modalRef.current.show();
  };

  const handleSubmitValidatedProcurementReturn = async () => {
    if (validateProcurementReturnFormRef.current) {
      try {
        const isSuccess =
          await validateProcurementReturnFormRef.current.submit();
        if (isSuccess) {
          modalRef.current.hide();
          setIsSubmitted(true);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleReturnToSupplier = (procurementReturnId: number) => {
    setModalProps({
      title: 'Return to Supplier',
      content: (
        <ReturnToSupplierDetailsForm
          ref={returnToSupplierDetailsFormRef}
          procurementReturnId={procurementReturnId}
        />
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleSubmitReturnToSupplier()
    });
    modalRef.current.show();
  };

  const handleSubmitReturnToSupplier = async () => {
    if (returnToSupplierDetailsFormRef.current) {
      try {
        const isSuccess = await returnToSupplierDetailsFormRef.current.submit();
        if (isSuccess) {
          modalRef.current.hide();
          setIsSubmitted(true);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleCancelReturn = (procurementReturnId: number) => {
    setModalProps({
      title: 'Cancel Return',
      content: (
        <CancelReturnDetailsForm
          ref={cancelReturnDetailsFormRef}
          procurementReturnId={procurementReturnId}
        />
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleSubmitCancelledReturn()
    });
    modalRef.current.show();
  };

  const handleSubmitCancelledReturn = async () => {
    if (cancelReturnDetailsFormRef.current) {
      try {
        const isSuccess = await cancelReturnDetailsFormRef.current.submit();
        if (isSuccess) {
          modalRef.current.hide();
          setIsSubmitted(true);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleRejectReturn = (procurementReturnId: number) => {
    setModalProps({
      title: 'Reject Return',
      content: (
        <RejectReturnDetailsForm
          ref={rejectReturnDetailsFormRef}
          procurementReturnId={procurementReturnId}
        />
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleSubmitRejectedReturn()
    });
    modalRef.current.show();
  };

  const handleSubmitRejectedReturn = async () => {
    if (rejectReturnDetailsFormRef.current) {
      try {
        const isSuccess = await rejectReturnDetailsFormRef.current.submit();
        if (isSuccess) {
          modalRef.current.hide();
          setIsSubmitted(true);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const fetchData = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    const { data } = await axiosInstance.get(
      `/api/v1/${selectedBranch?.branchDomain}/procurementReturns/`,
      {
        params: {
          page,
          pageSize,
          search,
          orderBy,
          orderByDesc,
          ...filters
        }
      }
    );
    return data;
  };

  const getSuppliers = async () => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/suppliers?page=1&pageSize=200`
      );
      setSuppliers(res.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  const columns: ColumnDef<TProcurementReturns>[] = [
    {
      id: 'supplierName',
      header: 'Supplier Name',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { supplierId, supplierName } = original;
        return (
          <Link
            to={`/supplier-details/${supplierId}`}
            className="text-decoration-none fw-bold fs-9"
          >
            {supplierName}
          </Link>
        );
      }
    },
    {
      id: 'productName',
      header: 'Product Name',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { productName, productId } = original;
        return (
          <Link
            to={`/product/${encodeURIComponent(
              selectedBranch?.branchDomain as string
            )}/${productId}`}
            className="text-decoration-none fw-bold fs-9"
          >
            {productName}
          </Link>
        );
      }
    },
    {
      id: 'procurementInvoiceNumber',
      header: 'Invoice Number',
      accessorKey: 'procurementInvoiceNumber',
      enableSorting: false
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { status } = original;
        return <BadgeStatus status={status} />;
      }
    },
    {
      id: 'quantity',
      header: 'Quantity',
      accessorKey: 'quantity',
      enableSorting: false
    },
    {
      id: 'unitPrice',
      header: 'Unit Price',
      accessorKey: 'unitPrice',
      enableSorting: false
    },
    {
      id: 'amount',
      header: 'Amount',
      accessorKey: 'amount',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { amount } = original;
        return <span>{amount?.toLocaleString()}</span>;
      }
    },
    {
      id: 'createdOn',
      accessorKey: 'createdOn',
      header: 'Created On',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const formattedDate =
          original.createdOn && formatDate(original.createdOn);
        return <span>{formattedDate}</span>;
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { procurementReturnId, status } = original;
        return (
          <>
            {status !== 'Cancelled' && status !== 'Rejected' && (
              <DropdownButton
                id="dropdown-basic-button"
                title=""
                variant="phoenix-primary"
              >
                {status !== 'Reviewed' && (
                  <Dropdown.Item
                    onClick={() => {
                      if (procurementReturnId !== undefined) {
                        validateProcurementReturn(+procurementReturnId);
                      }
                    }}
                  >
                    Validate Return
                  </Dropdown.Item>
                )}
                {status !== 'ReturnedToSupplier' && (
                  <Dropdown.Item
                    onClick={() => {
                      if (procurementReturnId !== undefined) {
                        handleReturnToSupplier(procurementReturnId);
                      }
                    }}
                  >
                    Return to Supplier
                  </Dropdown.Item>
                )}
                {status !== 'ReturnedToSupplier' && (
                  <Dropdown.Item
                    onClick={() => {
                      if (procurementReturnId !== undefined) {
                        handleCancelReturn(procurementReturnId);
                      }
                    }}
                  >
                    Cancel Return
                  </Dropdown.Item>
                )}
                <Dropdown.Item
                  onClick={() => {
                    if (procurementReturnId !== undefined) {
                      handleRejectReturn(procurementReturnId);
                    }
                  }}
                >
                  Reject Return
                </Dropdown.Item>
              </DropdownButton>
            )}
          </>
        );
      }
    }
  ];

  useEffect(() => {
    getSuppliers();
  }, []);

  useEffect(() => {
    if (isSubmitted) {
      setIsSubmitted(false);
    }
  }, [isSubmitted]);

  return (
    <>
      <DataTable
        queryKey={['procurementReturns', isSubmitted || filters]}
        queryFn={fetchData}
        columns={columns}
        rightSideActions={
          <>
            {suppliers && (
              <Form.Select
                style={{
                  height: '2.4rem',
                  width: '25%'
                }}
                value={filters.supplierId}
                onChange={e => handleFilterChange('supplierId', e.target.value)}
              >
                <option value="">All Supplier</option>
                {suppliers?.map(data => (
                  <option key={data.supplierId} value={data.supplierId}>
                    {data.name}
                  </option>
                ))}
              </Form.Select>
            )}
            {returnStatus && (
              <Form.Select
                style={{
                  height: '2.4rem',
                  width: '25%'
                }}
                value={filters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                {returnStatus?.map((option, index) => (
                  <option value={option.value} key={index}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            )}
            <Row lg="auto">
              <Button
                variant="primary"
                className="w-10 mb-3 me-5"
                onClick={createProcurementReturn}
              >
                Add Return
              </Button>
            </Row>
          </>
        }
      />
      <CustomModal
        ref={modalRef}
        title={modalProps?.title}
        content={modalProps?.content}
        onCancel={modalProps?.onCancel}
        onSubmit={modalProps?.onSubmit}
        modalSize="lg"
      />
    </>
  );
};

export default ReturnsListTab;
